import React, { useState, useEffect } from 'react';
import { Eye, FileText, Plus, MoreHorizontal, X, Upload } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { collection, query, where, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../config/firebase';
import { H1, H2, H3, H4, H5, H6 } from '../../components/typography';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alla statusar' },
  { value: 'TENANT_INFO_SUBMITTED', label: 'Väntar på kreditcheck' },
  { value: 'APPROVED', label: 'Godkänd av BRF' },
  { value: 'PENDING', label: 'Väntar på BRF' },
  { value: 'CREDIT_CHECK_COMPLETE', label: 'Kreditcheck klar' }
];

export default function CreditChecks() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [creditCheckComments, setCreditCheckComments] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [creditCheckAnswers, setCreditCheckAnswers] = useState({
    betalningsanmarkningar: '', // 'ja' eller 'nej'
    skuldsaldo: '', // 'ja' eller 'nej'
    inkomst: '' // 'ja' eller 'nej'
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  async function fetchApplications() {
    setLoading(true);
    setError(null);
    try {
      const ref = collection(db, 'applications');
      // Hämta både väntande kreditkontroller och avslutade
      const q1 = query(ref, where('status', '==', 'TENANT_INFO_SUBMITTED'));
      const q2 = query(ref, where('status', '==', 'CREDIT_CHECK_COMPLETE'));
      
      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      
      const allApps = [
        ...snap1.docs.map(doc => ({ id: doc.id, ...doc.data() })),
        ...snap2.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      ];
      
      setApplications(allApps);
    } catch (err) {
      setError('Kunde inte hämta ansökningar');
    } finally {
      setLoading(false);
    }
  }

  async function createTestApplications() {
    const testApplications = [
      {
        tenantFirstName: 'Anna',
        tenantLastName: 'Andersson',
        tenantEmail: 'anna.andersson@example.com',
        tenantPhoneNumber: '070-1234567',
        tenantPersonalNumber: '199001011234',
        selectedApartment: '1001 - Majvivsgatan 1A',
        housingCooperative: 'BRF Linné',
        fromDate: '2024-02-01',
        toDate: '2024-08-31',
        status: 'TENANT_INFO_SUBMITTED',
        reason: 'Tillfälligt arbete på annan ort',
        squareMeter: 65,
        rooms: 2,
        rentalFee: 15000,
        deposit: 30000,
        createdBy: 'test-user',
        createdAt: new Date().toISOString()
      },
      {
        tenantFirstName: 'Erik',
        tenantLastName: 'Eriksson',
        tenantEmail: 'erik.eriksson@example.com',
        tenantPhoneNumber: '070-9876543',
        tenantPersonalNumber: '198505151234',
        selectedApartment: '2003 - Majvivsgatan 1A',
        housingCooperative: 'BRF Linné',
        fromDate: '2024-03-01',
        toDate: '2024-09-30',
        status: 'APPROVED',
        reason: 'Studier på annan ort',
        squareMeter: 75,
        rooms: 3,
        rentalFee: 18000,
        deposit: 36000,
        createdBy: 'test-user',
        createdAt: new Date().toISOString()
      }
    ];
    for (const app of testApplications) {
      await addDoc(collection(db, 'applications'), app);
    }
    fetchApplications();
  }

  // KPI counts
  const pendingCount = applications.filter(a => a.status === 'TENANT_INFO_SUBMITTED').length;
  const completedCount = applications.filter(a => a.status === 'CREDIT_CHECK_COMPLETE').length;

  // Filtered applications (search only)
  let filtered = applications;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(a => 
      a.tenantFirstName?.toLowerCase().includes(s) ||
      a.tenantLastName?.toLowerCase().includes(s) ||
      a.selectedApartment?.toLowerCase().includes(s)
    );
  }

  async function handleCreditCheckComplete() {
    if (!selected || !pdfFile) return;

    // Debug logging
    console.log('Selected application creditCheck:', selected.creditCheck);
    console.log('creditCheck status:', selected.creditCheck?.status);

    // Validate that tenant has given consent
    if (!selected.creditCheck) {
      setError('⚠️ Kreditkontroll har inte initierats! Hyresgästen måste först få en förfrågan om samtycke.');
      alert('Fel: Kreditkontroll har inte initierats för denna ansökan. Kontrollera att hyresgästen har fått en förfrågan om samtycke.');
      return;
    }

    if (selected.creditCheck.status !== 'consent_given') {
      const statusText = selected.creditCheck.status === 'consent_pending'
        ? 'Hyresgästen har inte svarat på samtyckesbegäran ännu.'
        : `Oväntat status: ${selected.creditCheck.status}`;
      setError(`⚠️ Kan inte ladda upp kreditkontroll - ${statusText}`);
      alert(`Fel: ${statusText}\n\nHyresgästen måste ge sitt samtycke innan kreditkontroll kan laddas upp.`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // 1. Ladda upp PDF till Firebase Storage
      // // console.log('🔄 Laddar upp PDF till Firebase Storage...');
      const fileName = `credit-reports/${selected.id}_${Date.now()}.pdf`;
      const storageRef = ref(storage, fileName);
      
      // // console.log('📁 Fil som laddas upp:', pdfFile.name, 'Storlek:', pdfFile.size, 'bytes');
      const uploadResult = await uploadBytes(storageRef, pdfFile);
      // // console.log('✅ PDF uppladdad till Storage:', uploadResult.ref.fullPath);
      
      // 2. Hämta download URL
      const downloadURL = await getDownloadURL(uploadResult.ref);
      // // console.log('🔗 Download URL erhållen:', downloadURL);
      
      // 3. Uppdatera ansökan i Firestore med PDF URL, kommentarer och kreditcheck-svar
      await updateDoc(doc(db, 'applications', selected.id), {
        status: 'CREDIT_CHECK_COMPLETE',
        creditCheckComments,
        creditCheckDate: new Date().toISOString(),
        creditCheckPdfUrl: downloadURL,
        creditCheckPdfPath: fileName, // Spara även sökvägen för eventuell radering senare
        creditCheckAnswers: creditCheckAnswers, // Spara kreditcheck-svaren
        'creditCheck.status': 'uploaded', // Triggar notification till BRF
        'creditCheck.uploadedAt': new Date().toISOString()
      });
      
      // // console.log('✅ Ansökan uppdaterad i Firestore med PDF URL');
      
      // 4. Stäng modal och rensa formulär
      setModalOpen(false);
      setSelected(null);
      setCreditCheckComments('');
      setPdfFile(null);
      setCreditCheckAnswers({
        betalningsanmarkningar: '',
        skuldsaldo: '',
        inkomst: ''
      });
      fetchApplications();
      
    } catch (error) {
      setError(`Fel vid upload: ${error.message}`);
    } finally {
      setUploading(false);
    }
  }

  async function handleResendConsent(app) {
    if (resendingId) return;
    setResendingId(app.id);
    try {
      const resend = httpsCallable(functions, 'resendConsentEmail');
      const res = await resend({ applicationId: app.id });
      const data = res?.data || {};
      if (data.success) {
        alert(`Samtyckesmejl skickat på nytt till ${data.tenantEmail || 'hyresgästen'}.`);
      } else {
        alert(`Kunde inte skicka: ${data.error || 'Okänt fel'}`);
      }
    } catch (err) {
      alert(`Kunde inte skicka samtyckesmejl: ${err.message || 'Okänt fel'}`);
    } finally {
      setResendingId(null);
    }
  }

  // Ärenden utan samtyckesbegäran, t.ex. ett hyresgästbyte som gjordes innan
  // bytet själv började starta samtycket. Servern startar begäran och
  // hyresgästen får samma mejl som i vanliga flödet.
  async function handleStartConsent(app) {
    if (resendingId) return;
    setResendingId(app.id);
    try {
      const start = httpsCallable(functions, 'startTenantConsent');
      const res = await start({ applicationId: app.id });
      alert(`Samtyckesbegäran skickad till ${res?.data?.tenantEmail || 'hyresgästen'}.`);
      fetchApplications();
    } catch (err) {
      alert(`Kunde inte begära samtycke: ${err.message || 'Okänt fel'}`);
    } finally {
      setResendingId(null);
    }
  }

  function getStatusBadge(status) {
    const map = {
      'TENANT_INFO_SUBMITTED': { label: 'Väntar på kreditcheck', class: 'bg-yellow-100 text-yellow-800' },
      'APPROVED': { label: 'Godkänd av BRF', class: 'bg-green-100 text-green-800' },
      'PENDING': { label: 'Väntar på BRF', class: 'bg-blue-100 text-blue-800' },
      'CREDIT_CHECK_COMPLETE': { label: 'Kreditcheck klar', class: 'bg-green-100 text-green-800' }
    };
    const c = map[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${c.class}`}>
        {c.label}
      </span>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <H1>Kreditcheck Dashboard</H1>
          <p className="text-[#50535A] mt-1">Ladda upp kreditupplysningar för BRF Admin att granska</p>
        </div>
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={createTestApplications}
            className="flex items-center gap-2 px-4 py-2 border border-[#E2EAF0] rounded-lg text-[#50535A] hover:bg-[#F9FCFF] transition"
          >
            <Plus size={16} />
            Skapa test-data
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow p-5 flex items-center gap-4 animate-pulse">
            <div className="rounded-full bg-[#E2EAF0] p-3 w-12 h-12" />
            <div>
              <div className="h-6 w-16 bg-[#E2EAF0] rounded mb-2" />
              <div className="h-4 w-24 bg-[#E2EAF0] rounded" />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow p-5 flex flex-col items-center">
            <div className="text-lg font-semibold text-orange-600 mb-1">Väntar på kreditcheck</div>
            <div className="text-3xl font-bold text-[#222222]">{pendingCount}</div>
          </div>
          <div className="bg-white rounded-xl shadow p-5 flex flex-col items-center">
            <div className="text-lg font-semibold text-green-600 mb-1">Kreditcheck klar</div>
            <div className="text-3xl font-bold text-[#222222]">{completedCount}</div>
          </div>
        </div>
      )}

      {/* Filter row (only search) */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <input
          type="text"
          placeholder="Sök efter namn eller lägenhet..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="bg-white border border-[#E2EAF0] rounded-lg px-4 py-2 text-[#50535A]"
        />
      </div>

      {/* Loader och felmeddelande */}
      {loading && (
        <div className="py-8 flex justify-center">
          <span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#62B8E9]"></span>
        </div>
      )}
      {error && (
        <div className="text-red-500 text-center py-4">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-2xl shadow border border-[#E2EAF0]">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-[#50535A]">
              <th className="px-4 py-3 text-left font-medium">Hyresgäst</th>
              <th className="px-4 py-3 text-left font-medium">Lägenhet</th>
              <th className="px-4 py-3 text-left font-medium">BRF</th>
              <th className="px-4 py-3 text-left font-medium">Period</th>
              <th className="px-4 py-3 text-left font-medium">Åtgärder</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2EAF0]">
            {filtered.map(app => (
              <tr key={app.id} className="hover:bg-[#F9FCFF] transition">
                <td className="px-4 py-3 text-[#222222]">{app.tenantFirstName} {app.tenantLastName}</td>
                <td className="px-4 py-3 text-[#50535A]">{app.selectedApartment}</td>
                <td className="px-4 py-3 text-[#50535A]">{app.housingCooperative}</td>
                <td className="px-4 py-3 text-[#50535A]">
                  {new Date(app.fromDate).toLocaleDateString('sv-SE')} - {new Date(app.toDate).toLocaleDateString('sv-SE')}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    {app.creditCheckPdfUrl && (
                      <a
                        href={app.creditCheckPdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded-full hover:bg-green-100 transition text-green-600"
                        title="Ladda ner PDF"
                        aria-label="Ladda ner PDF"
                      >
                        <Eye className="w-5 h-5" />
                      </a>
                    )}
                    {app.status === 'TENANT_INFO_SUBMITTED' && (
                      <>
                        {app.creditCheck?.status === 'consent_given' ? (
                          <button
                            className="p-1.5 rounded-full hover:bg-[#E2EAF0] transition text-[#50535A]"
                            title="Ladda upp kreditcheck"
                            aria-label="Ladda upp kreditcheck"
                            onClick={() => { setSelected(app); setModalOpen(true); }}
                          >
                            <Upload className="w-5 h-5" />
                          </button>
                        ) : app.creditCheck?.status === 'consent_pending' ? (
                          <>
                            <span
                              className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full whitespace-nowrap"
                              title="Väntar på samtycke från hyresgäst"
                            >
                              ⚠️ Väntar på samtycke
                            </span>
                            <button
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white hover:bg-blue-600 transition-colors whitespace-nowrap disabled:opacity-50"
                              onClick={() => handleResendConsent(app)}
                              disabled={resendingId === app.id}
                              title="Skicka samtyckeslänken på nytt till hyresgästen"
                            >
                              {resendingId === app.id ? 'Skickar...' : '✉️ Skicka igen'}
                            </button>
                          </>
                        ) : (
                          <button
                            className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white hover:bg-blue-600 transition-colors whitespace-nowrap disabled:opacity-50"
                            onClick={() => handleStartConsent(app)}
                            disabled={resendingId === app.id}
                            title="Skicka en samtyckesbegäran till hyresgästen"
                          >
                            {resendingId === app.id ? 'Skickar...' : '📋 Begär samtycke'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={modalOpen} onClose={() => {
        setModalOpen(false);
        setSelected(null);
        setCreditCheckComments('');
        setPdfFile(null);
        setCreditCheckAnswers({
          betalningsanmarkningar: '',
          skuldsaldo: '',
          inkomst: ''
        });
        setError(null);
      }} className="fixed z-50 inset-0 overflow-y-auto">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-lg p-8 w-full max-w-2xl mx-auto flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
            <button
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
            onClick={() => {
              setModalOpen(false);
              setSelected(null);
              setCreditCheckComments('');
              setPdfFile(null);
              setCreditCheckAnswers({
                betalningsanmarkningar: '',
                skuldsaldo: '',
                inkomst: ''
              });
              setError(null);
            }}
            aria-label="Stäng"
          >
            <X className="w-5 h-5" />
          </button>
            {selected && (
              <>
                <H2 fontSize="22px" sx={{ marginBottom: '1rem' }}>Kreditcheck - {selected.tenantFirstName} {selected.tenantLastName}</H2>
                
                {/* Ansökningsdetaljer */}
                <div className="mb-6">
                  <H3 className="mb-2 flex items-center gap-2"><FileText className="h-5 w-5" /> Ansökningsdetaljer</H3>
                  <div className="grid grid-cols-2 gap-y-2 text-sm mb-2">
                  <div className="text-zinc-500 font-medium">Lägenhet</div>
                  <div className="text-zinc-900">{selected.selectedApartment}</div>
                  <div className="text-zinc-500 font-medium">Bostadsrättsförening</div>
                  <div className="text-zinc-900">{selected.housingCooperative}</div>
                  <div className="text-zinc-500 font-medium">Personnummer</div>
                  <div className="text-zinc-900">{selected.tenantPersonalNumber}</div>
                  <div className="text-zinc-500 font-medium">Telefon</div>
                  <div className="text-zinc-900">{selected.tenantPhoneNumber}</div>
                  <div className="text-zinc-500 font-medium">E-post</div>
                  <div className="text-zinc-900">{selected.tenantEmail}</div>
                  <div className="text-zinc-500 font-medium">Uthyrningsperiod</div>
                  <div className="text-zinc-900">{selected.fromDate} - {selected.toDate}</div>
                </div>
              </div>

              {/* Kreditcheck formulär */}
              <div className="mb-6">
                <H3 className="mb-2 flex items-center gap-2"><Upload className="h-5 w-5" /> Ladda upp kreditcheck</H3>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    {error}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Kreditcheck-rapport (PDF) *</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={e => setPdfFile(e.target.files[0])}
                      className="block w-full text-sm border border-zinc-200 rounded-lg p-2"
                      required
                      disabled={uploading}
                    />
                    {pdfFile && (
                      <div className="text-green-600 text-xs mt-1 flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {pdfFile.name} vald ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                      </div>
                    )}
                  </div>

                  {/* Kreditcheck frågor */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-zinc-700 mb-3">Kreditcheck bedömning *</h4>
                    
                    {/* Betalningsanmärkningar */}
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">Betalningsanmärkningar</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="betalningsanmarkningar"
                            value="ja"
                            checked={creditCheckAnswers.betalningsanmarkningar === 'ja'}
                            onChange={e => setCreditCheckAnswers(prev => ({
                              ...prev,
                              betalningsanmarkningar: e.target.value
                            }))}
                            className="text-[#62B8E9] focus:ring-[#62B8E9]"
                            disabled={uploading}
                          />
                          <span className="text-sm text-zinc-700">Ja</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="betalningsanmarkningar"
                            value="nej"
                            checked={creditCheckAnswers.betalningsanmarkningar === 'nej'}
                            onChange={e => setCreditCheckAnswers(prev => ({
                              ...prev,
                              betalningsanmarkningar: e.target.value
                            }))}
                            className="text-[#62B8E9] focus:ring-[#62B8E9]"
                            disabled={uploading}
                          />
                          <span className="text-sm text-zinc-700">Nej</span>
                        </label>
                      </div>
                    </div>

                    {/* Skuld saldo hos Kronofogden */}
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">Skuld saldo hos Kronofogden</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="skuldsaldo"
                            value="ja"
                            checked={creditCheckAnswers.skuldsaldo === 'ja'}
                            onChange={e => setCreditCheckAnswers(prev => ({
                              ...prev,
                              skuldsaldo: e.target.value
                            }))}
                            className="text-[#62B8E9] focus:ring-[#62B8E9]"
                            disabled={uploading}
                          />
                          <span className="text-sm text-zinc-700">Ja</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="skuldsaldo"
                            value="nej"
                            checked={creditCheckAnswers.skuldsaldo === 'nej'}
                            onChange={e => setCreditCheckAnswers(prev => ({
                              ...prev,
                              skuldsaldo: e.target.value
                            }))}
                            className="text-[#62B8E9] focus:ring-[#62B8E9]"
                            disabled={uploading}
                          />
                          <span className="text-sm text-zinc-700">Nej</span>
                        </label>
                      </div>
                    </div>

                    {/* Inkomst relevant till hyra */}
                    <div>
                      <label className="block text-xs text-zinc-500 mb-2">Inkomst relevant till hyra</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="inkomst"
                            value="ja"
                            checked={creditCheckAnswers.inkomst === 'ja'}
                            onChange={e => setCreditCheckAnswers(prev => ({
                              ...prev,
                              inkomst: e.target.value
                            }))}
                            className="text-[#62B8E9] focus:ring-[#62B8E9]"
                            disabled={uploading}
                          />
                          <span className="text-sm text-zinc-700">Ja</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="inkomst"
                            value="nej"
                            checked={creditCheckAnswers.inkomst === 'nej'}
                            onChange={e => setCreditCheckAnswers(prev => ({
                              ...prev,
                              inkomst: e.target.value
                            }))}
                            className="text-[#62B8E9] focus:ring-[#62B8E9]"
                            disabled={uploading}
                          />
                          <span className="text-sm text-zinc-700">Nej</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Kommentarer (valfritt)</label>
                    <textarea
                      value={creditCheckComments}
                      onChange={e => setCreditCheckComments(e.target.value)}
                      className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-zinc-700"
                      rows={3}
                      placeholder="Ange kommentarer om kreditchecken..."
                      disabled={uploading}
                    />
                  </div>
                </div>
              </div>

              <hr className="my-2" />
              <div className="flex gap-2 mt-2">
                <button
                  className="flex-1 border border-zinc-200 rounded-lg py-2 font-semibold text-zinc-700 hover:bg-zinc-50 transition"
                  onClick={() => {
                    setModalOpen(false);
                    setSelected(null);
                    setCreditCheckComments('');
                    setPdfFile(null);
                    setCreditCheckAnswers({
                      betalningsanmarkningar: '',
                      skuldsaldo: '',
                      inkomst: ''
                    });
                    setError(null);
                  }}
                  disabled={uploading}
                >
                  Avbryt
                </button>
                <button
                  className="flex-1 bg-[#62B8E9] text-white rounded-lg py-2 font-semibold hover:bg-[#5ba3d4] transition disabled:opacity-50 flex items-center justify-center gap-2"
                  onClick={handleCreditCheckComplete}
                  disabled={!pdfFile || uploading || 
                    !creditCheckAnswers.betalningsanmarkningar || 
                    !creditCheckAnswers.skuldsaldo || 
                    !creditCheckAnswers.inkomst}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Laddar upp...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Ladda upp kreditcheck
                    </>
                  )}
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}
